import type { Worksheet } from "exceljs";
import type { PrintoutColumn, PrintoutColumnType } from "../types";

interface ApplyVerticalMergesArgs {
	worksheet: Worksheet;
	startRow: number;
	endRow: number;
	expandedColumns: PrintoutColumn[];
	rowGroupingBy: PrintoutColumnType[];
}

/**
 * Post-pass that merges vertically adjacent cells with the same value in `rowGroupingBy` columns.
 *
 * Mapping §6.1 cell-merging axis. Walks each merge column independently and finds runs of cells
 * sharing the same value (and non-empty), merging each run into one cell with vertical/center alignment.
 *
 * No-op if `rowGroupingBy` is empty or the range is empty.
 */
export const applyVerticalMerges = ({ worksheet, startRow, endRow, expandedColumns, rowGroupingBy }: ApplyVerticalMergesArgs): void => {
	if (rowGroupingBy.length === 0 || endRow <= startRow) return;

	for (let colIdx = 0; colIdx < expandedColumns.length; colIdx++) {
		const col = expandedColumns[colIdx];
		if (!rowGroupingBy.includes(col.type)) continue;

		const excelCol = colIdx + 1;
		let runStart = startRow;
		let runValue = worksheet.getRow(startRow).getCell(excelCol).value;

		for (let r = startRow + 1; r <= endRow; r++) {
			const cellValue = worksheet.getRow(r).getCell(excelCol).value;
			if (cellValue === runValue && runValue !== "" && runValue != null) continue;

			if (r - 1 > runStart) {
				worksheet.mergeCells(runStart, excelCol, r - 1, excelCol);
				worksheet.getRow(runStart).getCell(excelCol).alignment = { vertical: "middle", horizontal: "left" };
			}
			runStart = r;
			runValue = cellValue;
		}
		if (endRow > runStart) {
			worksheet.mergeCells(runStart, excelCol, endRow, excelCol);
			worksheet.getRow(runStart).getCell(excelCol).alignment = { vertical: "middle", horizontal: "left" };
		}
	}
};
